import L from "leaflet"
import "leaflet/dist/leaflet.css"
import type { Agent, MapData } from "../types"
import type { AgentPosition } from "./animation"

// Import SVG icons
import coupleSvg from "../assets/agents/person/couple-mw.svg?raw"
import femaleSvg from "../assets/agents/person/female.svg?raw"
import maleSvg from "../assets/agents/person/male.svg?raw"

// Fix Leaflet default marker icon path issue
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow })

// SVG lookup map
const SVG_MAP: Record<string, string> = {
    "male.svg": maleSvg,
    "female.svg": femaleSvg,
    "couple-mw.svg": coupleSvg,
}

// F. Only these streets get name tooltips (no visible line drawn)
const LABELED_STREETS = new Set([
    "sveavägen",
    "klaratunneln",
    "olof palmes gata",
    "kungsgatan",
    "vasagatan",
    "tunnelgatan",
    "drottninggatan",
    "tegnérgatan",
])

export interface LeafletMap {
    map: L.Map
    tileLayer: L.TileLayer
    streetLayer: L.GeoJSON
    buildingLayer: L.GeoJSON
    waypointLayers: Map<string, L.LayerGroup>
    agentMarkers: Map<string, L.Marker>
    onWaypointDragEnd?: (agentId: string, segIdx: number, wpIdx: number, newPosition: [number, number]) => void
    onViewportPinDragEnd?: (pinId: string, newPosition: [number, number]) => void
    onViewportPinClick?: (pinId: string) => void
    onAgentClick?: (agentId: string, event: MouseEvent) => void
    selectedWaypoint?: { agentId: string; segIdx: number; wpIdx: number }
    destroy: () => void
}

const CARTO_BASEMAP_KEY = import.meta.env.VITE_CARTO_BASEMAP_KEY

function cartoTileUrl(style: "light_all" | "dark_all") {
    const key = CARTO_BASEMAP_KEY ? `?key=${encodeURIComponent(CARTO_BASEMAP_KEY)}` : ""
    return `https://{s}.basemaps.cartocdn.com/rastertiles/${style}/{z}/{x}/{y}{r}.png${key}`
}

export function createLeafletMap(
    container: HTMLDivElement,
    mapData: MapData,
    onMapClick?: (latlng: [number, number], shiftKey: boolean) => void,
    isDarkMode: boolean = false,
    initialCenter?: [number, number],
    initialZoom: number = 18
): LeafletMap {
    const center: L.LatLngExpression = initialCenter || [
        (mapData.bounds[0][0] + mapData.bounds[1][0]) / 2,
        (mapData.bounds[0][1] + mapData.bounds[1][1]) / 2,
    ]

    const map = L.map(container, { center, zoom: initialZoom, zoomControl: true })

    // Tile layer (light or dark based on isDarkMode)
    const tileUrl = isDarkMode
        ? cartoTileUrl("dark_all")
        : cartoTileUrl("light_all")

    const tileLayer = L.tileLayer(tileUrl, {
        attribution: "&copy; OSM &copy; CARTO",
        maxZoom: 20,
        subdomains: "abcd",
    }).addTo(map)

    // F. Street layer - invisible lines, only tooltips for labeled streets
    const streetLayer = L.geoJSON(mapData.streets, {
        style: () => ({ weight: 0, opacity: 0, fillOpacity: 0 }),
        onEachFeature: (feature, layer) => {
            const name = feature.properties?.name || ""
            if (name && LABELED_STREETS.has(name.toLowerCase())) {
                layer.bindTooltip(name, { sticky: true, className: "street-label" })
            }
        },
    }).addTo(map)

    // Building layer (colors adjusted for dark/light mode)
    const buildingStyle = isDarkMode
        ? { color: "#4a5568", weight: 0.5, fillColor: "#2d3748", fillOpacity: 0.3 }
        : { color: "#bbb", weight: 0.5, fillColor: "#e5e5e5", fillOpacity: 0.3 }

    const buildingLayer = L.geoJSON(mapData.buildings, {
        style: () => buildingStyle,
        onEachFeature: (feature, layer) => {
            const name = feature.properties?.name || feature.properties?.addr
            if (name) layer.bindTooltip(name, { sticky: true })
        },
    }).addTo(map)

    if (onMapClick) {
        map.on("click", (e: L.LeafletMouseEvent) => onMapClick([e.latlng.lat, e.latlng.lng], e.originalEvent.shiftKey))
    }

    return {
        map,
        tileLayer,
        streetLayer,
        buildingLayer,
        waypointLayers: new Map(),
        agentMarkers: new Map(),
        destroy: () => map.remove(),
    }
}

/** Dynamically switch between dark and light mode */
export function setDarkMode(lmap: LeafletMap, mapData: MapData, isDarkMode: boolean) {
    // Remove old tile layer
    lmap.map.removeLayer(lmap.tileLayer)

    // Add new tile layer
    const tileUrl = isDarkMode
        ? cartoTileUrl("dark_all")
        : cartoTileUrl("light_all")

    lmap.tileLayer = L.tileLayer(tileUrl, {
        attribution: "&copy; OSM &copy; CARTO",
        maxZoom: 20,
        subdomains: "abcd",
    }).addTo(lmap.map)

    // Update building layer colors
    lmap.map.removeLayer(lmap.buildingLayer)

    const buildingStyle = isDarkMode
        ? { color: "#4a5568", weight: 0.5, fillColor: "#2d3748", fillOpacity: 0.3 }
        : { color: "#bbb", weight: 0.5, fillColor: "#e5e5e5", fillOpacity: 0.3 }

    lmap.buildingLayer = L.geoJSON(mapData.buildings, {
        style: () => buildingStyle,
        onEachFeature: (feature, layer) => {
            const name = feature.properties?.name || feature.properties?.addr
            if (name) layer.bindTooltip(name, { sticky: true })
        },
    }).addTo(lmap.map)
}

/** Draw waypoints only for the selected agent */
export function drawAgentWaypoints(lmap: LeafletMap, agent: Agent, isSelected: boolean, showLabels: boolean = true) {
    const existing = lmap.waypointLayers.get(agent.id)
    if (existing) lmap.map.removeLayer(existing)

    // Only draw the selected agent's path in edit mode
    if (!isSelected) {
        lmap.waypointLayers.delete(agent.id)
        return
    }

    const allWaypoints = agent.segments.flatMap((s) => s.waypoints)
    if (allWaypoints.length === 0) return

    const layerGroup = L.layerGroup()

    // Initialize drag system on lmap if not already present
    if (!(lmap as any)._waypointDragSystem) {
        // Track shift key globally
        let shiftPressed = false
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Shift") shiftPressed = true
        }
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.key === "Shift") shiftPressed = false
        }
        document.addEventListener("keydown", onKeyDown)
        document.addEventListener("keyup", onKeyUp)

        // Track dragging state
        let dragState: { marker: L.CircleMarker; agentId: string; segIdx: number; wpIdx: number } | null = null

        const onMapMouseMove = (e: L.LeafletMouseEvent) => {
            if (!dragState) return
            dragState.marker.setLatLng(e.latlng)
        }

        const onMapMouseUp = (e: L.LeafletMouseEvent) => {
            if (!dragState) return

            const { marker, agentId, segIdx, wpIdx } = dragState
            dragState = null

            lmap.map.dragging.enable()

            const newLatLng = marker.getLatLng()
            const newPosition: [number, number] = [newLatLng.lat, newLatLng.lng]
            if (lmap.onWaypointDragEnd) {
                lmap.onWaypointDragEnd(agentId, segIdx, wpIdx, newPosition)
            }
        }

        lmap.map.on("mousemove", onMapMouseMove)
        lmap.map.on("mouseup", onMapMouseUp)

        // Store system on lmap
        ;(lmap as any)._waypointDragSystem = {
            shiftPressed: () => shiftPressed,
            setDragState: (state: any) => {
                dragState = state
            },
            cleanup: () => {
                document.removeEventListener("keydown", onKeyDown)
                document.removeEventListener("keyup", onKeyUp)
                lmap.map.off("mousemove", onMapMouseMove)
                lmap.map.off("mouseup", onMapMouseUp)
                delete (lmap as any)._waypointDragSystem
            },
        }
    }

    const dragSystem = (lmap as any)._waypointDragSystem

    agent.segments.forEach((seg, segIdx) => {
        if (seg.waypoints.length === 0) return

        const latlngs = seg.waypoints.map((wp) => [wp.position[0], wp.position[1]] as L.LatLngExpression)
        const isFirst = segIdx === 0

        L.polyline(latlngs, {
            color: agent.color,
            weight: 3,
            opacity: 0.9,
            dashArray: isFirst ? undefined : "8, 4",
        }).addTo(layerGroup)

        seg.waypoints.forEach((wp, idx) => {
            const isSegStart = idx === 0
            const isSegEnd = idx === seg.waypoints.length - 1
            const isGlobalStart = segIdx === 0 && idx === 0
            const isGlobalEnd = segIdx === agent.segments.length - 1 && isSegEnd
            const hasStop = wp.stopDuration > 0
            const isSelected =
                lmap.selectedWaypoint?.agentId === agent.id &&
                lmap.selectedWaypoint?.segIdx === segIdx &&
                lmap.selectedWaypoint?.wpIdx === idx
            const radius = isGlobalStart || isGlobalEnd ? 6 : hasStop ? 5 : 4

            let label = isGlobalStart
                ? `${agent.label} START`
                : isGlobalEnd
                  ? `${agent.label} END`
                  : isSegStart
                    ? `${agent.label} S${segIdx + 1}`
                    : `${agent.label} ${segIdx + 1}.${idx + 1}`
            if (hasStop) label += ` (${wp.stopDuration}s)`

            const marker = L.circleMarker([wp.position[0], wp.position[1]], {
                radius: isSelected ? radius + 2 : radius,
                color: isSelected ? "#3b82f6" : agent.color,
                fillColor: isSelected
                    ? "#3b82f6"
                    : isGlobalStart
                      ? "#22c55e"
                      : isGlobalEnd
                        ? "#ef4444"
                        : isSegStart
                          ? "#60a5fa"
                          : hasStop
                            ? "#facc15"
                            : agent.color,
                fillOpacity: 0.9,
                weight: isSelected ? 3 : 2,
            }).bindTooltip(
                isSelected
                    ? `${label}<br/><span style="font-size: 10px; color: #60a5fa;">⇧ Shift+Drag to move</span>`
                    : label,
                { permanent: showLabels || isSelected, direction: "top", offset: [0, -8] }
            )

            // Click to select waypoint
            marker.on("click", (e: L.LeafletMouseEvent) => {
                L.DomEvent.stopPropagation(e)
                lmap.selectedWaypoint = { agentId: agent.id, segIdx, wpIdx: idx }
                // Redraw to show selection
                drawAgentWaypoints(lmap, agent, true, showLabels)
            })

            // Start drag on mousedown with shift
            marker.on("mousedown", (e: L.LeafletMouseEvent) => {
                if (!dragSystem.shiftPressed() || !isSelected) return
                L.DomEvent.stopPropagation(e)
                dragSystem.setDragState({ marker, agentId: agent.id, segIdx, wpIdx: idx })
                lmap.map.dragging.disable()
            })

            // Change cursor on hover
            marker.on("mouseover", () => {
                const isWaypointSelected =
                    lmap.selectedWaypoint?.agentId === agent.id &&
                    lmap.selectedWaypoint?.segIdx === segIdx &&
                    lmap.selectedWaypoint?.wpIdx === idx
                ;(marker.getElement() as HTMLElement | undefined)?.style.setProperty(
                    "cursor",
                    isWaypointSelected ? "move" : "pointer"
                )
            })

            marker.addTo(layerGroup)
        })
    })

    layerGroup.addTo(lmap.map)
    lmap.waypointLayers.set(agent.id, layerGroup)
}

export function clearAgentWaypoints(lmap: LeafletMap, agentId: string) {
    const existing = lmap.waypointLayers.get(agentId)
    if (existing) {
        lmap.map.removeLayer(existing)
        lmap.waypointLayers.delete(agentId)
    }
}

// Calculate icon size based on zoom level
function getIconSizeForZoom(zoom: number): number {
    // Base size at zoom 16 - reduced to 1/4 of original (7.5px instead of 30px)
    const baseSize = 7.5
    const scaleFactor = Math.pow(1.4, zoom - 16)
    return Math.max(4, Math.min(20, baseSize * scaleFactor))
}

function createAgentDivIcon(agent: Agent, zoom: number): L.DivIcon {
    const iconSize = getIconSizeForZoom(zoom)

    let iconContent = ""

    if (agent.type === "person" && agent.svg) {
        const svgContent = SVG_MAP[agent.svg]
        if (svgContent) {
            // Wrap SVG in a div with explicit size and black color
            iconContent = `<div class="sim-svg-icon" style="width: ${iconSize}px; height: ${iconSize}px; color: #000000;">${svgContent}</div>`
        } else {
            // Fallback to emoji if SVG not found
            iconContent = `<span class="sim-emoji">${agent.emoji}</span>`
        }
    } else {
        // Non-person agents use emoji
        iconContent = `<span class="sim-emoji">${agent.emoji}</span>`
    }

    return L.divIcon({
        className: "sim-agent-icon",
        html: `<div class="sim-agent sim-type-${agent.type}" style="--icon-size: ${iconSize}px;">
      ${iconContent}
      <span class="sim-label">${agent.label}</span>
    </div>`,
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize / 2, iconSize / 2],
    })
}

export function updateAgentPositions(
    lmap: LeafletMap,
    positions: AgentPosition[],
    agentLookup: Map<string, Agent>,
    showLabels: boolean = true
) {
    const zoom = lmap.map.getZoom()

    for (const pos of positions) {
        const agent = agentLookup.get(pos.agentId)
        if (!agent) continue
        let marker = lmap.agentMarkers.get(pos.agentId)
        if (!pos.visible || !pos.position) {
            if (marker) {
                lmap.map.removeLayer(marker)
                lmap.agentMarkers.delete(pos.agentId)
            }
            continue
        }
        if (!marker) {
            // Create marker with synchronous icon
            const icon = createAgentDivIcon(agent, zoom)
            marker = L.marker([pos.position[0], pos.position[1]], {
                icon,
                zIndexOffset: 1000,
            }).addTo(lmap.map)

            // Add click handler with mousedown for more reliable clicking during movement
            marker.on("mousedown", (e: L.LeafletMouseEvent) => {
                L.DomEvent.stopPropagation(e)
                e.originalEvent?.preventDefault()
                if (lmap.onAgentClick) {
                    lmap.onAgentClick(pos.agentId, e.originalEvent)
                }
            })

            lmap.agentMarkers.set(pos.agentId, marker)
        } else {
            marker.setLatLng([pos.position[0], pos.position[1]])
        }
        // Toggle label: hidden when global labels off OR 3s before agent end
        const el = marker.getElement()
        if (el) {
            el.classList.toggle("label-hidden", !showLabels || pos.hideLabel)
        }
    }
}

// Update all agent icons when zoom changes
export function updateAgentIconsForZoom(lmap: LeafletMap, agentLookup: Map<string, Agent>) {
    const zoom = lmap.map.getZoom()

    for (const [agentId, marker] of lmap.agentMarkers) {
        const agent = agentLookup.get(agentId)
        if (agent) {
            const icon = createAgentDivIcon(agent, zoom)
            marker.setIcon(icon)
        }
    }
}

/**
 * Detect and hide overlapping agent labels to reduce visual clutter
 * Priority: emergency vehicles > non-person agents > person agents
 * Uses hysteresis to prevent label flickering
 */
let labelVisibilityState = new Map<string, { visible: boolean; lastChange: number }>()

export function detectAndHideOverlappingLabels(
    lmap: LeafletMap,
    agentLookup: Map<string, Agent>,
    speed: number = 1,
    immediate: boolean = false
) {
    const BASE_HYSTERESIS_MS = 4000 // Base 4 seconds hysteresis
    const HYSTERESIS_MS = BASE_HYSTERESIS_MS / Math.max(1, speed) // Adjust for speed
    const now = performance.now()

    interface LabelInfo {
        agentId: string
        element: HTMLElement
        bounds: DOMRect
        priority: number
    }

    const labels: LabelInfo[] = []

    // Collect all label elements and their bounding boxes
    for (const [agentId, marker] of lmap.agentMarkers) {
        const element = marker.getElement()
        if (!element) continue

        const labelElement = element.querySelector(".sim-label") as HTMLElement
        if (!labelElement) continue

        const agent = agentLookup.get(agentId)
        if (!agent) continue

        // Priority: emergency (3) > vehicles (2) > people (1)
        let priority = 1
        if (agent.type === "ambulance" || agent.type === "police") priority = 3
        else if (agent.type !== "person") priority = 2

        labels.push({
            agentId,
            element: labelElement,
            bounds: labelElement.getBoundingClientRect(),
            priority,
        })

        // Initialize state if not exists
        if (!labelVisibilityState.has(agentId)) {
            labelVisibilityState.set(agentId, { visible: true, lastChange: 0 })
        }
    }

    // Determine desired visibility for each label
    const desiredVisibility = new Map<string, boolean>()

    // Start with all visible
    labels.forEach(({ agentId }) => {
        desiredVisibility.set(agentId, true)
    })

    // Check for overlaps and mark lower priority labels as hidden
    for (let i = 0; i < labels.length; i++) {
        for (let j = i + 1; j < labels.length; j++) {
            const a = labels[i]
            const b = labels[j]

            // Check if bounding boxes overlap
            const overlaps = !(
                a.bounds.right < b.bounds.left ||
                a.bounds.left > b.bounds.right ||
                a.bounds.bottom < b.bounds.top ||
                a.bounds.top > b.bounds.bottom
            )

            if (overlaps) {
                // Mark the lower priority label as hidden
                if (a.priority < b.priority) {
                    desiredVisibility.set(a.agentId, false)
                } else if (b.priority < a.priority) {
                    desiredVisibility.set(b.agentId, false)
                } else {
                    // Same priority: hide the second one (arbitrary but consistent)
                    desiredVisibility.set(b.agentId, false)
                }
            }
        }
    }

    // Apply visibility with hysteresis and smooth transitions
    labels.forEach(({ agentId, element }) => {
        const state = labelVisibilityState.get(agentId)!
        const desired = desiredVisibility.get(agentId)!
        const timeSinceChange = now - state.lastChange

        // Only change visibility if enough time has passed since last change (or immediate mode)
        if (desired !== state.visible && (immediate || timeSinceChange > HYSTERESIS_MS)) {
            // Fade out fast (200ms), fade in slow (800ms ease-in) for less attention
            element.style.transition = desired ? "opacity 0.8s ease-in" : "opacity 0.2s ease-out"
            element.style.opacity = desired ? "1" : "0"
            element.style.pointerEvents = desired ? "auto" : "none"
            state.visible = desired
            state.lastChange = now
        } else if (timeSinceChange <= HYSTERESIS_MS) {
            // Keep current state during hysteresis period
            element.style.opacity = state.visible ? "1" : "0"
            element.style.pointerEvents = state.visible ? "auto" : "none"
        } else {
            // Apply current state
            element.style.opacity = state.visible ? "1" : "0"
            element.style.pointerEvents = state.visible ? "auto" : "none"
        }
    })
}

export function clearAllAgentMarkers(lmap: LeafletMap) {
    for (const [_id, marker] of lmap.agentMarkers) lmap.map.removeLayer(marker)
    lmap.agentMarkers.clear()
}

export function clearAllWaypoints(lmap: LeafletMap) {
    for (const [_id, layerGroup] of lmap.waypointLayers) {
        lmap.map.removeLayer(layerGroup)
    }
    lmap.waypointLayers.clear()
}

/**
 * Draw viewport pin markers on the map in edit mode
 */
export function drawViewportPinMarkers(
    lmap: LeafletMap,
    pins: Array<{ id: string; position: [number, number]; zoom: number }>,
    selectedPinId: string | null
) {
    // Remove existing pin markers
    lmap.map.eachLayer((layer: any) => {
        if (layer.options && layer.options.viewportPin) {
            lmap.map.removeLayer(layer)
        }
    })

    // Draw pin markers
    pins.forEach((pin, index) => {
        // Skip pins without valid positions
        if (pin.position[0] === 0 && pin.position[1] === 0) return

        const isSelected = pin.id === selectedPinId

        // Create circle marker:
        // - Selected: Filled circle (allows Shift+drag)
        // - Default: Small white circle
        const circle = L.circleMarker([pin.position[0], pin.position[1]], {
            radius: isSelected ? 8 : 6,
            color: "#6366f1", // indigo color for viewport pins
            fillColor: isSelected ? "#6366f1" : "white",
            fillOpacity: isSelected ? 0.9 : 0.3,
            weight: 2,
            viewportPin: true,
        } as any)

        circle.addTo(lmap.map)

        // Initialize viewport pin drag system if not present
        if (!(lmap as any)._viewportPinDragSystem) {
            let shiftPressed = false
            const onKeyDown = (e: KeyboardEvent) => {
                if (e.key === "Shift") shiftPressed = true
            }
            const onKeyUp = (e: KeyboardEvent) => {
                if (e.key === "Shift") shiftPressed = false
            }
            document.addEventListener("keydown", onKeyDown)
            document.addEventListener("keyup", onKeyUp)

            let dragState: { marker: L.CircleMarker; pinId: string } | null = null

            const onMapMouseMove = (e: L.LeafletMouseEvent) => {
                if (!dragState) return
                dragState.marker.setLatLng(e.latlng)
            }

            const onMapMouseUp = (e: L.LeafletMouseEvent) => {
                if (!dragState) return

                const { pinId } = dragState
                dragState = null
                lmap.map.dragging.enable()

                const newLatLng = e.latlng
                const newPosition: [number, number] = [newLatLng.lat, newLatLng.lng]
                if (lmap.onViewportPinDragEnd) {
                    lmap.onViewportPinDragEnd(pinId, newPosition)
                }
            }

            lmap.map.on("mousemove", onMapMouseMove)
            lmap.map.on("mouseup", onMapMouseUp)
            ;(lmap as any)._viewportPinDragSystem = {
                shiftPressed: () => shiftPressed,
                setDragState: (state: any) => {
                    dragState = state
                },
            }
        }

        const dragSystem = (lmap as any)._viewportPinDragSystem

        // Click to select pin
        circle.on("click", (e: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e)
            if (lmap.onViewportPinClick) {
                lmap.onViewportPinClick(pin.id)
            }
        })

        // Start drag on mousedown with shift (only when selected)
        circle.on("mousedown", (e: L.LeafletMouseEvent) => {
            if (!dragSystem.shiftPressed() || !isSelected) return
            L.DomEvent.stopPropagation(e)
            dragSystem.setDragState({ marker: circle, pinId: pin.id })
            lmap.map.dragging.disable()
        })

        // Change cursor on hover
        circle.on("mouseover", () => {
            ;(circle.getElement() as HTMLElement | undefined)?.style.setProperty(
                "cursor",
                isSelected ? "move" : "pointer"
            )
        })

        circle.on("mouseout", () => {
            ;(circle.getElement() as HTMLElement | undefined)?.style.setProperty("cursor", "")
        })

        // Add pin.svg icon above the circle (20% larger, with visible gap)
        const pinIcon = L.divIcon({
            html: `
                <div style="
                    width: 29px;
                    height: 29px;
                    color: black;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                    position: relative;
                ">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width: 100%; height: 100%;">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                    </svg>
                </div>
            `,
            className: "viewport-pin-icon",
            iconSize: [29, 29],
            iconAnchor: [14.5, 38], // Position pin above circle with visible gap
        })

        const iconMarker = L.marker([pin.position[0], pin.position[1]], {
            icon: pinIcon,
            viewportPin: true,
            interactive: false, // Disable interaction on icon marker
        } as any)

        iconMarker.addTo(lmap.map)

        // Add tooltip showing pin number
        const tooltipText = isSelected
            ? `Vyplats ${index + 1}<br/><span style="font-size: 10px; color: #6366f1;">⇧ Shift+Drag to move</span>`
            : `Vyplats ${index + 1}`

        circle.bindTooltip(tooltipText, {
            permanent: false,
            direction: "top",
            offset: [0, -10],
        })
    })
}

export function clearViewportPinMarkers(lmap: LeafletMap) {
    lmap.map.eachLayer((layer: any) => {
        if (layer.options && layer.options.viewportPin) {
            lmap.map.removeLayer(layer)
        }
    })
}
